import { useState, useEffect } from "react";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Users,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3
} from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

export default function AdminRevenue() {
  const { user } = useAdminBoard();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadRevenueData();
  }, []);

  const loadRevenueData = async () => {
    setLoading(true);
    const token = localStorage.getItem("speakr_admin_token");
    
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setData(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load revenue data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Failed to load revenue data</p>
        <Button onClick={loadRevenueData} className="mt-4">Retry</Button>
      </div>
    );
  }

  const totalActiveSubscribers = Object.values(data.active_subscriptions).reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Revenue Dashboard</h1>
          <p className="text-slate-500 mt-1">Billing and subscription analytics</p>
        </div>
        <Button onClick={loadRevenueData} variant="outline" data-testid="refresh-revenue">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="total-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Revenue</p>
                <p className="text-3xl font-bold text-slate-900">${data.total_revenue.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mt-1">All time</p>
              </div>
              <div className="p-2.5 rounded-lg bg-emerald-100">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="monthly-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Monthly Revenue</p>
                <p className="text-3xl font-bold text-slate-900">${data.monthly_revenue.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mt-1">This month</p>
              </div>
              <div className="p-2.5 rounded-lg bg-blue-100">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="mrr-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">MRR</p>
                <p className="text-3xl font-bold text-slate-900">${data.mrr.toLocaleString()}</p>
                <p className="text-sm text-slate-500 mt-1">Monthly recurring</p>
              </div>
              <div className="p-2.5 rounded-lg bg-purple-100">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="subscribers-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Subscribers</p>
                <p className="text-3xl font-bold text-slate-900">{totalActiveSubscribers}</p>
                <p className="text-sm text-slate-500 mt-1">Paid accounts</p>
              </div>
              <div className="p-2.5 rounded-lg bg-amber-100">
                <Users className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Transaction Stats & Revenue by Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Transaction Stats */}
        <Card data-testid="transaction-stats-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-slate-600" />
              <CardTitle>Transaction Statistics</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-900">{data.transactions.total}</p>
                <p className="text-sm text-slate-500">Total</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-700">{data.transactions.successful}</p>
                <p className="text-sm text-emerald-600">Successful</p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{data.transactions.failed}</p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-600">Success Rate</span>
                <span className="text-sm font-medium">{data.transactions.success_rate}%</span>
              </div>
              <Progress value={data.transactions.success_rate} className="h-2" />
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Plan */}
        <Card data-testid="revenue-by-plan-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-slate-600" />
              <CardTitle>Revenue by Plan</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.revenue_by_plan).length === 0 ? (
              <p className="text-slate-500 text-center py-4">No revenue data yet</p>
            ) : (
              Object.entries(data.revenue_by_plan).map(([plan, revenue]) => {
                const percentage = data.total_revenue > 0 
                  ? (revenue / data.total_revenue * 100) 
                  : 0;
                
                return (
                  <div key={plan} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium capitalize">{plan.replace(/_/g, " ")}</span>
                      <span className="font-bold">${revenue.toLocaleString()}</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active Subscriptions by Plan */}
      <Card data-testid="subscriptions-by-plan-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-600" />
            <CardTitle>Active Subscriptions by Plan</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.active_subscriptions).map(([plan, count]) => {
              const planColors = {
                free_trial: "bg-slate-100 text-slate-700",
                starter: "bg-blue-100 text-blue-700",
                professional: "bg-purple-100 text-purple-700",
                enterprise: "bg-amber-100 text-amber-700"
              };
              
              const planPrices = {
                free_trial: 0,
                starter: 49,
                professional: 99,
                enterprise: 299
              };
              
              return (
                <div 
                  key={plan} 
                  className={`p-4 rounded-lg ${planColors[plan] || "bg-slate-100 text-slate-700"}`}
                >
                  <p className="text-sm opacity-75 capitalize">{plan.replace(/_/g, " ")}</p>
                  <p className="text-3xl font-bold mt-1">{count}</p>
                  <p className="text-sm opacity-75 mt-1">
                    ${(planPrices[plan] || 0) * count}/mo
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
